import React from 'react';
import formatString from 'string-template';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { confirmation, length, format } from 'redux-form-validators';
import _, { chain, partial, split, omit, isEmpty, toArray } from 'lodash';

import {
    FormComponent,
    EmailInputFieldComponent,
    PasswordInputFieldComponent,
    TextInputFieldComponent,
    SelectFieldComponent,
    CheckboxInputFieldComponent,
    LegendFieldComponent
} from '../../../components/form';
import { PATH } from '../../../constants/path';
import { BRAND_NAME, STATIC_ROOT } from '../../../constants';
import { MADAME, MONSIEUR } from '../../../constants/civilities';
import { USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH, EMAIL_LENGTH } from '../../../constants/form';

import { registerUser } from '../../../services/authentication/register/actions';
import { fetchBankCreditCardIfNeeded } from '../../../services/bankcreditcard/actions';

import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';

import Captcha from '../../captcha/Captcha';
import SvgComponent from '../../SvgComponent';
import { SuccessInfoComponent } from '../../form/SuccessInfoComponent';

import './RegisterFormComponent.scss';

const CONFIRMATIONS_FIELD_NAME = ['emailconfirm', 'passwordconfirm', 'cguacceptance'];

class RegisterFormComponent extends React.Component {
    componentDidMount() {
        const { fetchBankCreditCardIfNeeded } = this.props;

        fetchBankCreditCardIfNeeded();
    }

    submit = values => {
        const { registerUser, history, source } = this.props;

        registerUser(omit(values, CONFIRMATIONS_FIELD_NAME), () => {
            if (!source || source !== 'app') {
                history.push(PATH.HOME_PATH);
            } else {
                this.scrollTop();
            }
        });
    };

    getCivilities = () => {
        const { i18nCommon } = this.props;

        return [
            { label: i18nCommon.CIVILITIES.MONSIEUR, value: MONSIEUR },
            { label: i18nCommon.CIVILITIES.MADAME, value: MADAME }
        ];
    };

    getBanks = () => {
        const { bankcreditcard } = this.props;

        return bankcreditcard.map(item => ({ label: item.bankLabel, value: item.bankId }));
    };

    scrollTop = () => {
        if (document.getElementById('entete')) {
            document.getElementById('entete').scrollIntoView({ behavior: 'smooth' });
        }
    };

    render() {
        const {
            errorMessage,
            i18n,
            i18nCommon,
            requestSubmit,
            successSubmit,
            errorSubmit,
            source
        } = this.props;

        if (!successSubmit) {
            return (
                <FormComponent
                    form="register-form"
                    onSubmit={this.submit}
                    requestSubmit={requestSubmit}
                    errorSubmit={errorSubmit}
                    className="register-form"
                    submitText={i18n.BUTTON_REGISTER}>
                    <TextInputFieldComponent
                        name="username"
                        classNameInputContainer="two-thirds"
                        placeholder={i18nCommon.PLACEHOLDER_USERNAME}
                        label={i18nCommon.LABEL_USERNAME}
                        isRequired
                        validate={[
                            length({
                                min: USERNAME_MIN_LENGTH,
                                msg: formatString(
                                    i18nCommon.USERNAME_SIZE_MIN_ERROR,
                                    USERNAME_MIN_LENGTH
                                )
                            }),
                            length({
                                max: USERNAME_MAX_LENGTH,
                                msg: formatString(
                                    i18nCommon.USERNAME_SIZE_MAX_ERROR,
                                    USERNAME_MAX_LENGTH
                                )
                            }),
                            format({
                                msg: formatString(i18nCommon.USERNAME_NO_SPACES_ERROR),
                                without: /\s/i
                            })
                        ]}
                    />
                    <SelectFieldComponent
                        name="civility"
                        classNameInputContainer="one-third"
                        label={i18nCommon.LABEL_CIVILITY}
                        defaultOptionLabel={i18nCommon.LABEL_CIVILITY}
                        options={this.getCivilities()}
                        isRequired
                    />
                    <EmailInputFieldComponent
                        name="email"
                        classNameInputContainer="two-thirds"
                        placeholder={i18nCommon.PLACEHOLDER_EMAIL}
                        label={i18nCommon.LABEL_EMAIL}
                        isRequired
                        validate={[
                            length({
                                max: EMAIL_LENGTH,
                                msg: formatString(i18nCommon.EMAIL_SIZE_ERROR, EMAIL_LENGTH)
                            })
                        ]}
                    />
                    <EmailInputFieldComponent
                        name="emailconfirm"
                        classNameInputContainer="two-thirds"
                        placeholder={i18n.EMAIL_CONFIRM}
                        isRequired
                        validate={[
                            confirmation({
                                field: 'email',
                                msg: i18n.EMAIL_CONFIRM_ERROR
                            })
                        ]}
                    />
                    <PasswordInputFieldComponent
                        name="password"
                        classNameInputContainer="two-thirds"
                        placeholder={i18nCommon.PLACEHOLDER_PASSWORD}
                        label={i18nCommon.LABEL_PASSWORD}
                        isRequired
                    />
                    <PasswordInputFieldComponent
                        name="passwordconfirm"
                        classNameInputContainer="two-thirds"
                        placeholder={i18n.PASSWORD_CONFIRM}
                        isRequired
                        validate={[
                            confirmation({
                                field: 'password',
                                msg: i18n.PASSWORD_CONFIRM_ERROR
                            })
                        ]}
                    />
                    <SelectFieldComponent
                        name="bank"
                        classNameInputContainer="two-thirds"
                        label={i18nCommon.LABEL_BANK}
                        defaultOptionLabel={i18nCommon.PLACEHOLDER_BANK}
                        options={this.getBanks()}
                        isRequired
                    />
                    <CheckboxInputFieldComponent
                        name="newsletterSubscribe"
                        options={[
                            { label: formatString(i18n.NEWSLETTER_TEXT, BRAND_NAME), value: 1 }
                        ]}
                    />
                    <CheckboxInputFieldComponent
                        name="cguacceptance"
                        options={[
                            {
                                label: (
                                    <span>
                                        {convertHtmlToReactDomWithParams(
                                            i18n.CGU_TEXT,
                                            PATH.TERMS_PATH,
                                            BRAND_NAME
                                        )}
                                    </span>
                                ),
                                value: 1
                            }
                        ]}
                        minRequired={1}
                    />
                    <LegendFieldComponent classNameInputContainer="full txtright">
                        {i18nCommon.TEXT_REQUIRED_FIELD}
                    </LegendFieldComponent>

                    <Captcha />

                    {errorMessage && (
                        <LegendFieldComponent classNameInputContainer="color-err">
                            {errorMessage}
                        </LegendFieldComponent>
                    )}
                </FormComponent>
            );
        } else {
            return (
                <SuccessInfoComponent>
                    <p className="success">
                        <SvgComponent
                            src={`${STATIC_ROOT}/toolbars/toolbar_check.svg`}
                            width="22"
                            height="22"
                        />
                        {i18n.REGISTER_SUCCESS_1}
                    </p>
                    {source === 'app' && <p>{i18n.REGISTER_SUCCESS_2}</p>}
                </SuccessInfoComponent>
            );
        }
    }
}

// Which props do we want to inject, given the global state?
function mapStateToProps(state, ownProps) {
    const search = ownProps.location.search;
    const params =
        search &&
        chain(search)
            .replace('?', '') // a=toto
            .split('&') // ["a=toto","c=bobo","f=coco"]
            .map(partial(split, _, '=', 2)) // [["a","toto"],["c","bobo"],["f","coco"]]
            .fromPairs() // {"a":"toto","c":"bobo","f":"coco"}
            .value();
    const source = params.source;

    return {
        errorMessage: state.authentication.error,
        requestSubmit: state.authentication.fetching || false,
        successSubmit: state.authentication.isAuthenticated,
        errorSubmit: !isEmpty(state.authentication.error),
        i18n: state.i18n.page.REGISTER,
        i18nCommon: state.i18n.feature.form,
        bankcreditcard: toArray(state.bankcreditcard.bankcreditcard),
        source: source
    };
}

const mapDispatchToProps = {
    registerUser,
    fetchBankCreditCardIfNeeded
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RegisterFormComponent));



// WEBPACK FOOTER //
// ./src/components/authentication/register/RegisterFormComponent.js